import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { assetUrl } from '../utils/assetUrl';
import { capturePosthogEvent } from '../services/posthogService';

/**
 * ConnectPage — ported from the consultant's connect.html ("Talk with Peter").
 *
 * Landing target for outreach links, which usually carry `utm_*` params
 * (PostHog picks those up from the pageview automatically) and a `cid`
 * param meant for the Tally form, not for us. Tally's embed.js forwards the
 * page's query string into the form, so `cid` reaches Tally without any code
 * here — don't strip the URL before the embed loads.
 *
 * `?f=FORMID` overrides the default Tally form per link.
 *
 * noindex is applied via an `X-Robots-Tag` header on `/connect` in
 * firebase.json (the SPA shares one index.html, so no per-page meta).
 */

const DEFAULT_TALLY_FORM_ID = 'kbaJpj';
const CALENDLY_URL = 'https://calendly.com/peter-homeplated/30-minute-call';
const PAGE_TITLE = 'Talk with Peter | HomePlated';

// Colors only apply on paid Calendly plans; free plans ignore them.
const CALENDLY_EMBED_URL = CALENDLY_URL +
  '?hide_event_type_details=1&hide_gdpr_banner=1' +
  '&background_color=f7f2e5&text_color=2a1609&primary_color=8f4420';

declare global {
  interface Window {
    Tally?: { loadEmbeds: () => void };
    Calendly?: { initInlineWidget: (opts: { url: string; parentElement: HTMLElement }) => void };
  }
}

/** Append a third-party script once; resolves on load or error. */
const loadScriptOnce = (src: string): Promise<void> =>
  new Promise((resolve) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${src}"]`);
    if (existing) {
      if (existing.dataset.loaded) resolve();
      else {
        existing.addEventListener('load', () => resolve());
        existing.addEventListener('error', () => resolve());
      }
      return;
    }
    const s = document.createElement('script');
    s.src = src;
    s.async = true;
    s.addEventListener('load', () => { s.dataset.loaded = '1'; resolve(); });
    s.addEventListener('error', () => resolve());
    document.body.appendChild(s);
  });

export const ConnectPage: React.FC = () => {
  const tallyRef = useRef<HTMLIFrameElement>(null);
  const calendlyRef = useRef<HTMLDivElement>(null);
  const bookRef = useRef<HTMLElement>(null);

  // Page title — restored on leave so other routes keep the site default.
  useEffect(() => {
    const prev = document.title;
    document.title = PAGE_TITLE;
    return () => { document.title = prev; };
  }, []);

  // Tally survey embed.
  useEffect(() => {
    const iframe = tallyRef.current;
    if (!iframe) return;
    const f = new URLSearchParams(window.location.search).get('f');
    const formId = f && /^[A-Za-z0-9]{1,12}$/.test(f) ? f : DEFAULT_TALLY_FORM_ID;
    iframe.setAttribute('data-tally-src', 'https://tally.so/embed/' + encodeURIComponent(formId) +
      '?alignLeft=1&hideTitle=1&transparentBackground=1&dynamicHeight=1');

    let cancelled = false;
    loadScriptOnce('https://tally.so/widgets/embed.js').then(() => {
      if (cancelled) return;
      if (window.Tally) window.Tally.loadEmbeds();
      else iframe.src = iframe.getAttribute('data-tally-src') ?? '';
    });
    return () => { cancelled = true; };
  }, []);

  // Calendly booking embed. Initialized explicitly (no `.calendly-inline-widget`
  // auto-init class) so first loads and in-app revisits take the same path.
  useEffect(() => {
    const el = calendlyRef.current;
    if (!el) return;
    let cancelled = false;
    loadScriptOnce('https://assets.calendly.com/assets/external/widget.js').then(() => {
      if (cancelled || !window.Calendly || el.querySelector('iframe')) return;
      window.Calendly.initInlineWidget({ url: CALENDLY_EMBED_URL, parentElement: el });
    });
    return () => { cancelled = true; };
  }, []);

  // Widget messages: Calendly height sizing + conversion events for both.
  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      if (e.origin === 'https://calendly.com') {
        const data = e.data as { event?: string; payload?: { height?: string } } | null;
        if (!data?.event) return;
        // Size the widget to Calendly's reported content height so it never
        // scrolls inside itself. Tiny heights are sent while still loading.
        if (data.event === 'calendly.page_height') {
          const h = parseInt(data.payload?.height ?? '', 10);
          if (h > 300 && calendlyRef.current) calendlyRef.current.style.height = h + 'px';
        } else if (data.event === 'calendly.event_scheduled') {
          capturePosthogEvent('connect_call_booked');
        }
        return;
      }
      if (e.origin === 'https://tally.so' && typeof e.data === 'string' && e.data.includes('Tally.FormSubmitted')) {
        try {
          const data = JSON.parse(e.data) as { event?: string; payload?: { formId?: string } };
          if (data.event === 'Tally.FormSubmitted') {
            capturePosthogEvent('connect_survey_submitted', { form_id: data.payload?.formId });
          }
        } catch {
          // Not a Tally JSON message — ignore.
        }
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  // Scroll in place rather than via `#book` so the URL (and PostHog
  // pageviews) aren't touched by a hash change.
  const scrollToBook = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    bookRef.current?.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth' });
  };

  return (
    <>
      {/* HERO */}
      <section className="section section-brown hp-hero-pad-lg" style={{ position: 'relative', overflow: 'hidden' }}>
        <div className="ghost-logo">
          <img src={assetUrl('/logo-cream.svg')} alt="" width="618" height="100" />
        </div>
        <div className="container" style={{ position: 'relative', zIndex: '2', maxWidth: '760px' }}>
          <div className="eyebrow eyebrow-orange" style={{ marginBottom: '22px' }}>HomePlated</div>
          <h1 style={{ marginBottom: '24px' }}>Hey, I'm Peter.</h1>
          <p className="lead" style={{ opacity: '0.85', maxWidth: '60ch', margin: '0 0 24px' }}>
            Thanks for reaching out. If you have a minute, take the short survey below, then grab a time to talk with me. I'm happy to spend as much time with you as you need.
          </p>
          <a href="#book" onClick={scrollToBook} className="link-fade" style={{ display: 'inline-block', padding: '8px 0', fontSize: '15px', fontWeight: '600', color: 'var(--hp-cream)' }}>Just want to talk? Book a time ↓</a>
        </div>
      </section>

      {/* SURVEY */}
      <section className="section section-cream">
        <div className="container" style={{ maxWidth: '760px' }}>
          <h2 style={{ marginBottom: '12px' }}>A few quick questions</h2>
          <p style={{ fontSize: '17px', color: 'rgba(42,22,9,.72)', margin: '0 0 28px' }}>Takes about 2 minutes. At the end, you'll pick a time to talk.</p>
          <iframe ref={tallyRef} loading="lazy" width="100%" height="500" frameBorder="0" marginHeight={0} marginWidth={0} title="Survey" />
        </div>
      </section>

      {/* DIRECT BOOKING */}
      <section ref={bookRef} id="book" className="section section-warm" style={{ scrollMarginTop: 'var(--nav-h, 72px)' }}>
        <div className="container" style={{ maxWidth: '1000px' }}>
          <div style={{ maxWidth: '696px', margin: '0 auto 28px' }}>
            <h2 style={{ marginBottom: '12px' }}>Rather skip the survey?</h2>
            <p style={{ fontSize: '17px', color: 'rgba(42,22,9,.72)', margin: '0' }}>Pick a time that works for you, and we'll talk.</p>
          </div>
          <div ref={calendlyRef} style={{ minWidth: '320px', height: '620px' }} />
        </div>
      </section>

      {/* ABOUT */}
      <section className="section section-cream">
        <div className="container" style={{ maxWidth: '1100px' }}>
          <div className="hp-grid-gap-md" style={{ display: 'grid', gridTemplateColumns: '340px 1fr', alignItems: 'center' }}>
            <div>
              <img src={assetUrl('/photos/PeterAndPaige.jpg')} alt="Peter &amp; Paige, founders" width="400" height="400" loading="lazy" style={{ width: '100%', maxWidth: '340px', height: 'auto', aspectRatio: '1', objectFit: 'cover', borderRadius: '28px' }} />
              <div style={{ marginTop: '16px' }}>
                <strong style={{ display: 'block', fontSize: '18px' }}>Peter &amp; Paige</strong>
                <div style={{ fontSize: '13px', opacity: '0.55', marginTop: '4px' }}>Founders, HomePlated<sup className="tm">™</sup></div>
              </div>
            </div>
            <div>
              <div className="eyebrow eyebrow-orange" style={{ marginBottom: '18px' }}>About us</div>
              <h2 style={{ marginBottom: '24px' }}>Neighbors feeding neighbors</h2>
              <div style={{ fontSize: '18px', lineHeight: '1.65', color: 'rgba(42,22,9,.82)', display: 'grid', gap: '20px', marginBottom: '32px' }}>
                <p style={{ margin: '0' }}>
                  Every community has untapped abundance hidden right inside its kitchens. Every block has a chef with a signature dish worth sharing, and every family has moments when dinner is one more burden than they can carry.
                </p>
                <p style={{ margin: '0' }}>
                  HomePlated<sup className="tm">™</sup> exists to close that gap: to turn neighbors into nourishment, talent into opportunity, and extra food into extraordinary impact.
                </p>
              </div>
              <Link to="/" className="btn btn-orange">Learn More</Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};
